import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import classnames from 'classnames';
import { defaultTo } from 'lodash-es';
import { CmsEntities } from 'client/data/models/cms';
import { transformContentForFaq } from 'site-modules/shared/components/faq/utils';
import { WRAPPER_TYPES } from 'site-modules/shared/components/faq/constants';
import { Faq } from 'site-modules/shared/components/faq/faq';
import { RelatedContent } from 'site-modules/shared/components/editorial/related-content/related-content';
import { TruncatedHtml } from 'site-modules/shared/components/editorial/truncated-html/truncated-html';
import { HeroContent } from 'site-modules/editorial/components/hero-content/hero-content';
import { AdEntry } from 'site-modules/editorial/components/ad-entry/ad-entry';
import { ArticlePhoto } from 'site-modules/shared/components/editorial/article-photo/article-photo';
import { ArticleVideo } from 'site-modules/editorial/components/article-video/article-video';
import { ArticleVideos } from 'site-modules/editorial/components/article-videos/article-videos';
import {
  ENTRY_ID,
  ENTRY_RENDER_TYPE,
  HERO_PHOTO_SIZE,
  TRUNCATED_HTML_HEIGHT,
} from 'site-modules/editorial/constants/best';
import { RELATED_CONTENT_CONFIG } from 'site-modules/shared/constants/editorial/best';
import { VehiclesSection } from 'site-modules/editorial/components/best/vehicles-section/vehicles-section';
import { ByLine } from 'site-modules/editorial/components/best/by-line/by-line';
import { ArticleLinks } from 'site-modules/editorial/components/best/article-links/article-links';
import { CompareModule } from 'site-modules/editorial/components/best/compare-module/compare-module';
import { ArticleOffersAd } from 'site-modules/shared/components/native-ad/article-module-ad/article-offers-ad';
import { ArticleTable } from 'site-modules/shared/components/editorial/article-table/article-table';
import { SuggestedVehiclePricingCarouselContainer } from 'site-modules/shared/components/suggested-vehicle-pricing-carousel/suggested-vehicle-pricing-carousel-container';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { ReadyForEvTreehouse } from 'site-modules/shared/components/ready-for-ev/ready-for-ev';
import { TreehousePromotionCheck } from 'site-modules/shared/components/treehouse-promotion/treehouse-promotion-check';
import { BestLeaseDealsCard } from 'site-modules/shared/components/best-lease-deals-card/best-lease-deals-card';

const MOBILE_PLATFORM = 'mobile';
const NO_SEPARATOR_ENTRY_ID = [ENTRY_ID.HERO_CONTENT];
const NO_SEPARATOR_RENDER_TYPE = [ENTRY_RENDER_TYPE.PHOTO];

export function ResearchEntry({
  entry,
  hasSeparator = false,
  onSelectCar = null,
  selectedCars = null,
  entriesProps,
  isRightRail = false,
  isMobile = false,
  componentToUse = null,
  hasTreehousePromo = false,
}) {
  const [forceNoSeparator, setForceNoSeparator] = useState(
    NO_SEPARATOR_ENTRY_ID.includes(entry.id) || NO_SEPARATOR_RENDER_TYPE.includes(entry.metadata('renderType').value())
  );

  function renderContentModule(module) {
    return (
      <Fragment>
        {isRightRail && <article className="is-content-module">{module}</article>}
        {!isRightRail && module}
      </Fragment>
    );
  }

  const { title, canonical, pathname, contentId, pageTitle } = entriesProps;
  const entryPath = defaultTo(entriesProps.entryPath, []);
  const Tag = componentToUse;

  const platform = entry.metadata('platform').value();
  if (platform && (platform === MOBILE_PLATFORM) !== isMobile) {
    return null;
  }

  const entryRenderType = entry.metadata('renderType').value();
  let renderEntry;

  if (entryRenderType) {
    switch (entryRenderType) {
      case ENTRY_RENDER_TYPE.AD: {
        renderEntry = (
          <AdEntry
            entry={entry}
            isMobile={isMobile}
            toggleForceNoSeparator={setForceNoSeparator}
            className={classnames('mb-1_5', {
              'd-flex flex-column justify-content-between': isRightRail,
            })}
            style={
              isRightRail
                ? {
                    height: 'auto!important ',
                    flexGrow: '1',
                  }
                : null
            }
          />
        );
        break;
      }
      case ENTRY_RENDER_TYPE.ARTICLE_MENU:
        renderEntry = renderContentModule(<ArticleLinks entry={entry} canonical={canonical} />);
        break;
      case ENTRY_RENDER_TYPE.CARD: {
        renderEntry = renderContentModule(
          <VehiclesSection
            entry={entry}
            entriesProps={entriesProps}
            onSelectCar={onSelectCar}
            selectedCars={selectedCars}
            isMobile={isMobile}
          />
        );
        break;
      }
      case ENTRY_RENDER_TYPE.HTML: {
        if (entry.metadata('htmlContent').value()) {
          renderEntry = renderContentModule(<TruncatedHtml entry={entry} minHeight={TRUNCATED_HTML_HEIGHT} />);
        }
        break;
      }
      case ENTRY_RENDER_TYPE.PHOTO: {
        renderEntry = renderContentModule(<ArticlePhoto photos={entry.links()} className="mb-2" />);
        break;
      }
      case ENTRY_RENDER_TYPE.RELATED_CONTENT: {
        renderEntry = renderContentModule(
          <RelatedContent entry={entry} config={RELATED_CONTENT_CONFIG} isMobile={isMobile} />
        );
        break;
      }
      case ENTRY_RENDER_TYPE.VIDEO:
        renderEntry = renderContentModule(<ArticleVideo entry={entry} />);
        break;
      case ENTRY_RENDER_TYPE.VIDEOS:
        renderEntry = renderContentModule(<ArticleVideos entry={entry} isMobile={isMobile} />);
        break;
      case ENTRY_RENDER_TYPE.TABLE:
        renderEntry = renderContentModule(
          <ArticleTable
            entry={entry}
            shareOptions={{
              contentId,
              canonical,
              entryPath: [...entryPath, entry.id],
              pageTitle,
            }}
          />
        );
        break;
      case ENTRY_RENDER_TYPE.ARTICLE_OFFERS_AD: {
        renderEntry = <ArticleOffersAd entry={entry} className="my-1" isMobile={isMobile} />;
        break;
      }
      case ENTRY_RENDER_TYPE.BEST_LEASE_DEALS_CARD: {
        renderEntry = <BestLeaseDealsCard entry={entry} isMobile={isMobile} />;
        break;
      }
      default: {
        break;
      }
    }
  } else {
    switch (entry.id) {
      case ENTRY_ID.AUTHOR: {
        renderEntry = (
          <MinHeightWrapper componentType="ARTICLE_BY_LINE" forceStaticHeight>
            <ByLine authorEntry={entry.child('author-info')} title={title} canonical={canonical} />
          </MinHeightWrapper>
        );
        break;
      }
      case ENTRY_ID.PRICING_CARDS: {
        renderEntry = (
          <Fragment>
            {hasTreehousePromo && (
              <TreehousePromotionCheck>
                <Row className="mb-3_5">
                  <Col xs={12} md={8}>
                    <div className="bg-cool-gray-90 px-1 py-1 rounded-12">
                      <ReadyForEvTreehouse
                        className="bg-white rounded-12 px-1_5 py-1_5"
                        heading="Am I Ready for an EV?"
                        creativeId="treehouse-promotion"
                      />
                    </div>
                  </Col>
                </Row>
              </TreehousePromotionCheck>
            )}
            <MinHeightWrapper
              className="suggested-pricing-wrapper show-separator"
              componentType="SUGGESTED_PRICING_CAROUSEL"
            >
              <SuggestedVehiclePricingCarouselContainer className="px-0" />
            </MinHeightWrapper>
          </Fragment>
        );
        break;
      }
      case ENTRY_ID.FAQ: {
        renderEntry = (
          <Faq
            {...transformContentForFaq(entry)}
            wrapperType={WRAPPER_TYPES.accordion}
            titleComponent="h3"
            questionComponent="h4"
            pathname={pathname}
            canonical={canonical}
            generateLearnMoreLinks
            learnMoreLinkClassName="fw-bold"
          />
        );
        break;
      }
      case ENTRY_ID.HERO_CONTENT: {
        renderEntry = (
          <HeroContent
            className="mb-1_5"
            entry={entry}
            alt="Hero"
            heroSize={isMobile ? HERO_PHOTO_SIZE.MOBILE : HERO_PHOTO_SIZE.DESKTOP}
            isMobile={isMobile}
          />
        );
        break;
      }
      case ENTRY_ID.COMPARE: {
        renderEntry = <CompareModule entry={entry} />;
        break;
      }
      default: {
        break;
      }
    }
  }

  if (!renderEntry) {
    return null;
  }

  return (
    <Fragment>
      {Tag ? <Tag>{renderEntry}</Tag> : renderEntry}
      {!forceNoSeparator && hasSeparator && <hr className="my-1_5 mb-md-2" />}
    </Fragment>
  );
}

ResearchEntry.propTypes = {
  entry: CmsEntities.Content.isRequired,
  entriesProps: PropTypes.shape({
    title: PropTypes.string,
    canonical: PropTypes.string,
    pathname: PropTypes.string,
    contentId: PropTypes.string,
    entryPath: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  hasSeparator: PropTypes.bool,
  isMobile: PropTypes.bool,
  onSelectCar: PropTypes.func,
  selectedCars: PropTypes.arrayOf(PropTypes.object),
  isRightRail: PropTypes.bool,
  componentToUse: PropTypes.string,
  hasTreehousePromo: PropTypes.bool,
};
